import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isNative } from '../utils/native';
import { Loader2 } from 'lucide-react';

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { exchangeSession, setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use useRef to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const sessionId = new URLSearchParams(hash.replace('#', '')).get('session_id');

        if (sessionId) {
          const userData = await exchangeSession(sessionId);
          // Priority: role select → building setup → role-based dashboard.
          const needsRoleSelect = userData?.role_selected === false;
          const needsBuildingSetup = userData?.role === 'resident' && !userData?.hoa_id;
          // Navigate based on user role (native app forces resident dashboard)
          if (needsRoleSelect) {
            navigate('/select-role', { replace: true });
          } else if (isNative) {
            navigate(needsBuildingSetup ? '/find-building' : '/dashboard', { replace: true });
          } else if (userData?.role === 'platform_owner') {
            navigate('/platform', { replace: true });
          } else if (['managing_agent', 'agent_admin'].includes(userData?.role)) {
            navigate('/agent', { replace: true });
          } else if (['property_manager', 'trustee'].includes(userData?.role)) {
            navigate('/admin', { replace: true });
          } else if (needsBuildingSetup) {
            navigate('/find-building', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        } else {
          navigate('/login', { replace: true });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [location, exchangeSession, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-sky-500 mx-auto mb-4" />
        <p className="text-slate-600 font-medium">Signing you in...</p>
      </div>
    </div>
  );
}
